import React from 'react';
import { TafHeader, TafMenu, TafMenuItem } from '@taf-ui/react';

import Language from 'enums/Language';
import LanguageOptions from 'enums/LanguageOptions';
import { isBrand, translate } from '../utils/utils';
import AppStorage from '../Storage/Storage';
import ErrorScreenContacts from '../ErrorScreen/ErrorScreenContacts';
import Labels from '../enums/Labels';
import LanguageSelector from '../LanguageSelector/LanguageSelector';
import wefoxLogo from '../images/wefox.png';
import DHMRLogo from '../images/dhmrLogo.png';
import TAFLogo from '../images/logo-taf.png';
import Brand from '../enums/Brand';

import './nonAuthorized.css';

export enum NonAuthorizedMode {
  ID_MISSED = 'ID_MISSED',
  INVALID_QS = 'INVALID_QS',
  KEYCLOAK_LOGOUT = 'KEYCLOAK_LOGOUT',
  MAINTENANCE = 'MAINTENANCE',
}

export const UNIQUE_URL_MESSAGE = Labels.NON_AUTHORIZED_UNIQUE_URL;
export const INVALID_QS_MESSAGE = {
  reason: Labels.NON_AUTHORIZED_INVALID_QS1,
  contact: Labels.NON_AUTHORIZED_INVALID_QS2,
};
export const LOGOUT_TITLE = Labels.NON_AUTHORIZED_LOGOUT_TITLE;
export const LOGOUT_RETURN_LINK = Labels.NON_AUTHORIZED_LOGOUT_RETURN_LINK;
export const KEYCLOAK_LOGOUT = Labels.NON_AUTHORIZED_KEYCLOAK_LOG_OUT;
export const {
  MAINTENANCE_HEADER,
  MAINTENANCE_P1,
  MAINTENANCE_P2,
  MAINTENANCE_P3,
  MAINTENANCE_P4,
} = Labels;

interface NonAuthorizedProps {
  mode: NonAuthorizedMode;
}

interface NonAuthorizedState {
  lang: Language;
}

interface NonAuthorizedChildProps {
  lang: Language;
  companyName?: string;
}

const IDMissed = ({ lang }: NonAuthorizedChildProps): React.ReactElement => (
  <div className="non-authorized">
    <h1>{translate(UNIQUE_URL_MESSAGE, lang)}</h1>
  </div>
);

const InvalidQS = ({
  lang,
  companyName = '',
}: NonAuthorizedChildProps): React.ReactElement => (
  <div>
    <h3>{translate(Labels.ERROR_HEADER, lang)}</h3>
    <p className="errorScreenMessage">
      {translate(INVALID_QS_MESSAGE.reason, lang)}
      <br />
      {translate(INVALID_QS_MESSAGE.contact, lang, [companyName])}
    </p>
    <ErrorScreenContacts />
  </div>
);

const KeycloakLogout = ({
  lang,
}: NonAuthorizedChildProps): React.ReactElement => (
  <div>
    <h3>{translate(LOGOUT_TITLE, lang)}</h3>
    <div className="errorScreenMessage">
      {translate(KEYCLOAK_LOGOUT, lang)}

      <p>{translate(LOGOUT_RETURN_LINK, lang)}</p>
    </div>
  </div>
);

const Maintenance = ({
  lang,
  companyName = '',
}: NonAuthorizedChildProps): React.ReactElement => (
  <div>
    <h3>{translate(MAINTENANCE_HEADER, lang)}</h3>
    <div className="errorScreenMessage">
      <p>{translate(MAINTENANCE_P1, lang)}</p>
      <p>{translate(MAINTENANCE_P2, lang)}</p>
      <p>
        <div>{translate(MAINTENANCE_P3, lang)}</div>
        <div>{translate(MAINTENANCE_P4, lang, [companyName])}</div>
      </p>
    </div>
  </div>
);

class NonAuthorized extends React.PureComponent<
  NonAuthorizedProps,
  NonAuthorizedState
> {
  private readonly storage: AppStorage;

  constructor(props: NonAuthorizedProps) {
    super(props);
    this.storage = AppStorage.getInstance();

    const locale = localStorage.getItem('locales');
    const supportedLocales = locale?.split(',') || ['nl'];

    const lang = supportedLocales[0].toUpperCase() as Language;

    this.state = {
      lang,
    };
  }

  getMessage = (): React.ReactElement => {
    const { mode } = this.props;
    const { lang } = this.state;

    const productSpecificData = this.storage.getProductSpecificInfo;

    return (
      <>
        {
          {
            ID_MISSED: <IDMissed lang={lang} />,
            INVALID_QS: (
              <InvalidQS
                lang={lang}
                companyName={productSpecificData.companyName}
              />
            ),
            KEYCLOAK_LOGOUT: <KeycloakLogout lang={lang} />,
            MAINTENANCE: (
              <Maintenance
                lang={lang}
                companyName={productSpecificData.companyName}
              />
            ),
          }[mode]
        }
      </>
    );
  };

  changeLangHandler = (event: LanguageOptions): void => {
    const lang: Language = event.value;
    this.setState({
      lang,
    });
    this.storage.setLocale(lang);
  };

  render(): React.ReactElement {
    const { lang } = this.state;
    const message = this.getMessage();

    const isWefox = isBrand(Brand.wefox);
    const isDHMR = isBrand(Brand.DHMR);
    const isTAF = !isWefox && !isDHMR;

    return (
      <div className="App">
        <div className="App-header">
          <TafHeader hideIconBar={isWefox}>
            {isWefox ? (
              <div className="logo-container" slot="logo-container">
                <img src={wefoxLogo} alt="wefox" />
              </div>
            ) : null}

            {isDHMR ? (
              <div className="logo-container" slot="logo-container">
                <img src={DHMRLogo} alt="DHMR" />
              </div>
            ) : null}

            {isTAF ? (
              <div className="logo-container" slot="logo-container">
                <img src={TAFLogo} alt="TAF" />
              </div>
            ) : null}

            <TafMenu slot="menu" noHoverState={true} noBorders={true}>
              <TafMenuItem>
                <div className="language">
                  <LanguageSelector
                    value={lang}
                    changeLangHandler={this.changeLangHandler}
                  />
                </div>
              </TafMenuItem>
            </TafMenu>
            <div className="toggle-menu-wrapper" slot="toggle-menu">
              <TafMenu
                className="toggle-menu"
                noBorders={true}
                noHoverState={true}
              >
                <TafMenuItem>
                  <div className="language">
                    <LanguageSelector
                      value={lang}
                      changeLangHandler={this.changeLangHandler}
                    />
                  </div>
                </TafMenuItem>
              </TafMenu>
            </div>
          </TafHeader>
        </div>
        <div className="App-navigation" />
        <div className="App-content">{message}</div>
      </div>
    );
  }
}

export default NonAuthorized;
